import * as Switch from "@radix-ui/react-switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router";

import { ApiError, errorText } from "../../api/client";
import { listCompanies } from "../../api/companies";
import type { NoReason, Outcome } from "../../api/companies";
import type { Profile } from "../../api/me";
import { createReport, undoReport } from "../../api/reports";
import type { PersonalDataFinding, ReportCreate, ReportCreated } from "../../api/reports";
import { Button, buttonClass } from "../../components/Button";
import { Combobox } from "../../components/Combobox";
import { FormError } from "../../components/Field";
import { useToast } from "../../components/Toast";
import { nl } from "../../i18n/nl";
import { ME_KEY } from "../auth/session";
import { COMPANIES_KEY } from "../companies/queries";
import { PROFILES } from "../profile/PreferenceFields";
import prefsStyles from "../profile/prefs.module.css";
import styles from "./report.module.css";

const t = nl.report;
const OUTCOMES: readonly Outcome[] = ["nee", "solliciteer", "ja_voor_mij", "zoeken_nog", "geen_reactie", "tip"];
const REASONS: readonly NoReason[] = ["it_extern", "geen_stages", "geen_begeleider", "al_volzet", "anders"];
const CHANNELS = ["telefoon", "mail", "ter_plaatse"] as const;
const TIP_MAX = 280;
const NOTE_MAX = 140;

export interface ReportCompany {
  id: string;
  name: string;
}

interface Draft {
  company: ReportCompany | null;
  outcome: Outcome | null;
  reason: NoReason | null;
  reasonNote: string;
  forEveryone: boolean;
  scope: Profile[];
  mspName: string;
  spotsTotal: string;
  spotsFilled: string;
  channel: (typeof CHANNELS)[number] | null;
  tipText: string;
  anonymous: boolean;
}

function emptyDraft(company: ReportCompany | null): Draft {
  return {
    company,
    outcome: null,
    reason: null,
    reasonNote: "",
    forEveryone: true,
    scope: [],
    mspName: "",
    spotsTotal: "",
    spotsFilled: "",
    channel: null,
    tipText: "",
    anonymous: true,
  };
}

const hasSpots = (outcome: Outcome | null) => outcome === "ja_voor_mij" || outcome === "zoeken_nog";

function spotsProblem(draft: Draft): boolean {
  if (!hasSpots(draft.outcome)) return false;
  const total = draft.spotsTotal === "" ? null : Number(draft.spotsTotal);
  const filled = draft.spotsFilled === "" ? null : Number(draft.spotsFilled);
  if (total !== null && total < 1) return true;
  if (total !== null && filled !== null && filled > total) return true;
  return draft.outcome === "zoeken_nog" && total !== null && filled !== null && filled >= total;
}

/** Same rules as the API (docs/PRODUCT.md § Uitkomsten); the button stays off until they hold. */
export function isValid(draft: Draft): boolean {
  if (!draft.company || !draft.outcome) return false;
  if (draft.outcome === "nee") {
    if (!draft.reason) return false;
    if (draft.reason === "anders" && !draft.reasonNote.trim()) return false;
    if (!draft.forEveryone && draft.scope.length === 0) return false;
  }
  return !spotsProblem(draft);
}

function toBody(draft: Draft, confirm: boolean): ReportCreate {
  const body: ReportCreate = { outcome: draft.outcome ?? "tip", anonymous: draft.anonymous, confirm };
  if (draft.outcome === "nee") {
    body.reason = draft.reason;
    if (draft.reason === "anders") body.reason_note = draft.reasonNote.trim();
    if (!draft.forEveryone) body.scope_profiles = draft.scope;
    if (draft.reason === "it_extern" && draft.mspName.trim()) body.msp_name = draft.mspName.trim();
  }
  if (hasSpots(draft.outcome)) {
    if (draft.spotsTotal !== "") body.spots_total = Number(draft.spotsTotal);
    if (draft.spotsFilled !== "") body.spots_filled = Number(draft.spotsFilled);
  }
  if (draft.channel) body.channel = draft.channel;
  if (draft.tipText.trim()) body.tip_text = draft.tipText.trim();
  return body;
}

function findingsOf(error: unknown): PersonalDataFinding[] {
  if (!(error instanceof ApiError) || error.code !== "contains_personal_data") return [];
  const findings = error.details?.findings;
  return Array.isArray(findings) ? (findings as PersonalDataFinding[]) : [];
}

function Choice({
  name,
  checked,
  onChange,
  label,
  text,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  text?: string;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className={[styles.choice, checked && styles.choiceOn].filter(Boolean).join(" ")}>
      <input id={id} type="radio" name={name} checked={checked} onChange={onChange} />
      <span className={styles.choiceLabel}>{label}</span>
      {text && <span className={styles.choiceText}>{text}</span>}
    </label>
  );
}

function Group({ legend, children, help }: { legend: string; children: ReactNode; help?: string }) {
  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>{legend}</legend>
      {help && <p className={styles.help}>{help}</p>}
      {children}
    </fieldset>
  );
}

/** Highlights what the filter found, so the student sees what to remove. */
function Findings({ text, findings }: { text: string; findings: PersonalDataFinding[] }) {
  const kinds = [...new Set(findings.map((f) => f.kind in t.personalKinds ? t.personalKinds[f.kind as keyof typeof t.personalKinds] : f.kind))];
  const parts: ReactNode[] = [];
  let at = 0;
  for (const finding of [...findings].sort((a, b) => a.start - b.start)) {
    if (finding.start < at) continue;
    parts.push(text.slice(at, finding.start));
    parts.push(<mark key={finding.start}>{text.slice(finding.start, finding.end)}</mark>);
    at = finding.end;
  }
  parts.push(text.slice(at));
  return (
    <div className={styles.findings}>
      <p className={styles.findingsKinds}>{kinds.join(", ")}</p>
      <p className={styles.findingsText}>{parts}</p>
    </div>
  );
}

function Confirmation({
  created,
  company,
  onUndone,
  onAnother,
}: {
  created: ReportCreated;
  company: ReportCompany;
  onUndone: () => void;
  onAnother: () => void;
}) {
  const toast = useToast();
  const client = useQueryClient();
  const undo = useMutation({
    mutationFn: () => undoReport(created.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: COMPANIES_KEY });
      void client.invalidateQueries({ queryKey: ME_KEY });
      toast({ message: t.undone });
      onUndone();
    },
  });
  return (
    <section className={styles.done} aria-labelledby="report-done-title" aria-live="polite">
      <h2 id="report-done-title" className={styles.doneTitle} tabIndex={-1}>
        {t.doneTitles[created.outcome]}
      </h2>
      <p className={styles.help}>{t.doneTexts[created.outcome]}</p>
      <FormError message={undo.error ? errorText(undo.error) : null} />
      <div className={styles.actions}>
        <Link className={buttonClass("primary")} to={`/bedrijven/${company.id}`}>
          {t.toCompany}
        </Link>
        <Button onClick={onAnother}>{t.another}</Button>
        <Button
          variant="ghost"
          disabled={undo.isPending}
          onClick={() => {
            undo.mutate();
          }}
        >
          {t.undo}
        </Button>
      </div>
    </section>
  );
}

export function ReportForm({ initialCompany = null }: { initialCompany?: ReportCompany | null }) {
  const client = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(initialCompany));
  const [more, setMore] = useState(false);
  const [created, setCreated] = useState<ReportCreated | null>(null);
  const update = (patch: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const send = useMutation({
    mutationFn: (confirm: boolean) => createReport(draft.company?.id ?? "", toBody(draft, confirm)),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: COMPANIES_KEY });
      void client.invalidateQueries({ queryKey: ME_KEY });
      setCreated(result);
      toast({
        message: t.sent,
        onUndo: () => {
          void undoReport(result.id).then(() => {
            void client.invalidateQueries({ queryKey: COMPANIES_KEY });
            setCreated(null);
          });
        },
      });
    },
  });
  const findings = findingsOf(send.error);
  const otherError = send.error && findings.length === 0 ? errorText(send.error) : null;

  if (created && draft.company) {
    return (
      <Confirmation
        created={created}
        company={draft.company}
        onUndone={() => {
          setCreated(null);
          send.reset();
        }}
        onAnother={() => {
          setCreated(null);
          send.reset();
          setDraft(emptyDraft(null));
        }}
      />
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (isValid(draft)) send.mutate(false);
  }

  const tipFindings = findings.filter((f) => f.field === "tip_text");
  const noteFindings = findings.filter((f) => f.field === "reason_note");
  return (
    <form className={styles.form} onSubmit={submit} noValidate aria-label={t.title}>
      {draft.company && initialCompany ? (
        <div className={styles.company}>
          <span className={styles.legend}>{t.company}</span>
          <div className={styles.companyRow}>
            <span className={styles.companyName}>{draft.company.name}</span>
            <Button
              variant="ghost"
              onClick={() => {
                update({ company: null });
              }}
            >
              {t.companyChange}
            </Button>
          </div>
        </div>
      ) : (
        <Combobox<ReportCompany>
          label={t.company}
          placeholder={t.companyPlaceholder}
          value={draft.company?.name ?? null}
          queryKey="report-company"
          search={async (term) =>
            (await listCompanies({ q: term, limit: 8 })).items.map((c) => ({
              key: c.id,
              primary: c.name,
              secondary: `${nl.status[c.status]} · ${c.municipality}`,
              data: { id: c.id, name: c.name },
            }))
          }
          onSelect={(option) => {
            update({ company: option.data });
          }}
          noneText={t.companyNone}
          searchingText={nl.prefs.municipalitySearching}
        />
      )}

      <Group legend={t.outcome}>
        <div className={styles.choices}>
          {OUTCOMES.map((outcome) => (
            <Choice
              key={outcome}
              name="outcome"
              checked={draft.outcome === outcome}
              onChange={() => {
                update({ outcome });
              }}
              label={t.outcomes[outcome].label}
              text={t.outcomes[outcome].text}
            />
          ))}
        </div>
      </Group>

      {draft.outcome === "nee" && (
        <>
          <Group legend={t.reason}>
            <div className={styles.choices}>
              {REASONS.map((reason) => (
                <Choice
                  key={reason}
                  name="reason"
                  checked={draft.reason === reason}
                  onChange={() => {
                    update({ reason });
                  }}
                  label={t.reasons[reason]}
                />
              ))}
            </div>
          </Group>
          {draft.reason === "anders" && (
            <TextArea
              label={t.reasonNote}
              help={t.reasonNoteHelp}
              max={NOTE_MAX}
              rows={2}
              value={draft.reasonNote}
              onChange={(reasonNote) => {
                update({ reasonNote });
              }}
            >
              {noteFindings.length > 0 && <Findings text={draft.reasonNote} findings={noteFindings} />}
            </TextArea>
          )}
          {draft.reason === "it_extern" && (
            <TextInput
              label={t.mspName}
              help={t.mspHelp}
              max={200}
              value={draft.mspName}
              onChange={(mspName) => {
                update({ mspName });
              }}
            />
          )}
          <Group legend={t.scopeQuestion}>
            <div className={styles.segments}>
              <Choice
                name="scope"
                checked={draft.forEveryone}
                onChange={() => {
                  update({ forEveryone: true });
                }}
                label={t.scopeAll}
              />
              <Choice
                name="scope"
                checked={!draft.forEveryone}
                onChange={() => {
                  update({ forEveryone: false });
                }}
                label={t.scopeSome}
              />
            </div>
            {!draft.forEveryone && (
              <div className={styles.checks}>
                {PROFILES.filter((p) => p !== "onbekend").map((profile) => (
                  <CheckBox
                    key={profile}
                    label={nl.prefs.profiles[profile].label}
                    checked={draft.scope.includes(profile)}
                    onChange={(on) => {
                      update({ scope: on ? [...draft.scope, profile] : draft.scope.filter((p) => p !== profile) });
                    }}
                  />
                ))}
              </div>
            )}
          </Group>
        </>
      )}

      {hasSpots(draft.outcome) && (
        <Group legend={t.spots}>
          <div className={styles.twoColumns}>
            <TextInput
              label={t.spotsTotal}
              type="number"
              min={1}
              max={50}
              value={draft.spotsTotal}
              onChange={(spotsTotal) => {
                update({ spotsTotal });
              }}
            />
            <TextInput
              label={t.spotsFilled}
              type="number"
              min={0}
              max={50}
              value={draft.spotsFilled}
              onChange={(spotsFilled) => {
                update({ spotsFilled });
              }}
            />
          </div>
          {spotsProblem(draft) && (
            <p className={styles.error} role="alert">
              {nl.errors.invalid_spots}
            </p>
          )}
        </Group>
      )}

      <div className={styles.more}>
        <Button
          variant="ghost"
          aria-expanded={more}
          onClick={() => {
            setMore(!more);
          }}
        >
          {more ? t.less : t.more}
        </Button>
        {more && (
          <div className={styles.moreBody}>
            <Group legend={t.channel}>
              <div className={styles.segments}>
                {CHANNELS.map((channel) => (
                  <Choice
                    key={channel}
                    name="channel"
                    checked={draft.channel === channel}
                    onChange={() => {
                      update({ channel });
                    }}
                    label={nl.companies.channels[channel]}
                  />
                ))}
              </div>
            </Group>
            <TextArea
              label={t.tip}
              help={t.tipHelp}
              max={TIP_MAX}
              rows={3}
              value={draft.tipText}
              onChange={(tipText) => {
                update({ tipText });
              }}
            >
              {tipFindings.length > 0 && <Findings text={draft.tipText} findings={tipFindings} />}
            </TextArea>
          </div>
        )}
      </div>

      <AnonymousSwitch
        checked={draft.anonymous}
        onChange={(anonymous) => {
          update({ anonymous });
        }}
      />

      {findings.length > 0 && (
        <div className={styles.warning} role="alert">
          <p className={styles.warningTitle}>{t.personalTitle}</p>
          <p>{t.personalText}</p>
          <Button
            disabled={send.isPending}
            onClick={() => {
              send.mutate(true);
            }}
          >
            {t.personalConfirm}
          </Button>
        </div>
      )}
      <FormError message={otherError} />
      <Button type="submit" variant="primary" size="large" disabled={!isValid(draft) || send.isPending}>
        {send.isPending ? t.submitting : t.submit}
      </Button>
    </form>
  );
}

function TextInput({
  label,
  help,
  max,
  value,
  onChange,
  type = "text",
  min,
}: {
  label: string;
  help?: string;
  max: number;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number";
  min?: number;
}) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.legend}>
        {label}
      </label>
      {help && (
        <p id={`${id}-help`} className={styles.help}>
          {help}
        </p>
      )}
      <input
        id={id}
        className={styles.input}
        type={type}
        inputMode={type === "number" ? "numeric" : undefined}
        min={type === "number" ? min : undefined}
        max={type === "number" ? max : undefined}
        maxLength={type === "text" ? max : undefined}
        aria-describedby={help ? `${id}-help` : undefined}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </div>
  );
}

function TextArea({
  label,
  help,
  max,
  rows,
  value,
  onChange,
  children,
}: {
  label: string;
  help: string;
  max: number;
  rows: number;
  value: string;
  onChange: (value: string) => void;
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.legend}>
        {label}
      </label>
      <p id={`${id}-help`} className={styles.help}>
        {help}
      </p>
      <textarea
        id={id}
        className={styles.textarea}
        rows={rows}
        maxLength={max}
        aria-describedby={`${id}-help ${id}-count`}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <p id={`${id}-count`} className={styles.count} aria-live="polite">
        {t.tipCount(value.length, max)}
      </p>
      {children}
    </div>
  );
}

function CheckBox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (on: boolean) => void }) {
  const id = useId();
  return (
    <label htmlFor={id} className={[styles.choice, checked && styles.choiceOn].filter(Boolean).join(" ")}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <span className={styles.choiceLabel}>{label}</span>
    </label>
  );
}

function AnonymousSwitch({ checked, onChange }: { checked: boolean; onChange: (on: boolean) => void }) {
  const id = useId();
  return (
    <div className={prefsStyles.switchRow}>
      <label htmlFor={id} className={prefsStyles.switchLabel}>
        <span className={styles.choiceLabel}>{t.anonymous}</span>
        <span className={styles.choiceText}>{t.anonymousHelp}</span>
      </label>
      <Switch.Root id={id} className={prefsStyles.switch} checked={checked} onCheckedChange={onChange}>
        <Switch.Thumb className={prefsStyles.thumb} />
      </Switch.Root>
    </div>
  );
}
